import { Table } from 'src/components/molecules/Table';
import { handleErrorToast } from 'src/components/molecules/Toast';
import { useGetReadURL } from 'src/components/molecules/UploadedEvidence/hooks/useGetReadURL';
import { FileEvidenceForDataDeletionType } from 'src/components/pages/Case/Case.slice';
import { UploadScanStatus } from 'src/types';
import {
  TableFileNameCellLink,
  UploadedFilesContainer,
} from './UploadedFiles.styles';

interface Props {
  files: FileEvidenceForDataDeletionType[];
  reportId: string;
  caseManagerView?: boolean;
  'data-testid'?: string;
  baseFileName?: string;
  downloadEvidenceDisabled?: boolean;
  selectedFiles: string[];
  setSelectedFiles: (files: string[]) => unknown;
}

export const UploadedFiles = ({
  files,
  reportId,
  baseFileName = 'EvidenceUploaded',
  downloadEvidenceDisabled,
  selectedFiles,
  setSelectedFiles,
  ...props
}: Props) => {
  const { getReadUrl } = useGetReadURL(true, reportId);

  const handleSelectAllFiles = () => {
    const allFileIds = files.map((file) => file.fileS3Path);
    const allSelected = allFileIds.every((id) => selectedFiles.includes(id));

    setSelectedFiles(
      allSelected
        ? selectedFiles.filter((id) => !allFileIds.includes(id))
        : [
            ...selectedFiles,
            ...allFileIds.filter((id) => !selectedFiles.includes(id)),
          ],
    );
  };

  const isAllFilesSelected = () => {
    return (
      files?.length > 0 &&
      files.every((file) => selectedFiles.includes(file.fileS3Path))
    );
  };

  const handleOpenFile = async (file: FileEvidenceForDataDeletionType) => {
    const cleanStatus = [
      UploadScanStatus.CLEAN,
      UploadScanStatus.MANUALLY_APPROVED,
    ];
    if (!cleanStatus.includes(file.status) || downloadEvidenceDisabled) return;
    try {
      const url = await getReadUrl(file);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      handleErrorToast({
        toastMessage: 'Failed to open file. Please try again',
      });
    }
  };

  const tableData = files.map((file, index) => {
    const defaultFileName = `${baseFileName}${index + 1}`;
    const fileNameText = `${defaultFileName}.${file.fileExtension}`;
    const canDownloadEvidence =
      !downloadEvidenceDisabled &&
      (file.status === UploadScanStatus.CLEAN ||
        file.status === UploadScanStatus.MANUALLY_APPROVED);

    const nameCell = (
      <div data-testid={`uploaded-evidence-file-name-${index}`}>
        {canDownloadEvidence ? (
          <TableFileNameCellLink
            onClick={() => handleOpenFile(file)}
            data-testid={`uploaded-evidence-file-link-${index}`}
          >
            {fileNameText}
          </TableFileNameCellLink>
        ) : (
          fileNameText
        )}
      </div>
    );

    const addedCell = (
      <div data-testid={`uploaded-evidence-file-submission-time-${index}`}>
        {file.isPreSubmission ? 'Time of submission' : 'Post submission'}
      </div>
    );

    return {
      cellData: [nameCell, addedCell],
      row: file.fileS3Path,
      data: file,
    };
  });

  return (
    <UploadedFilesContainer data-testid={props['data-testid']}>
      <Table
        columns={[{ label: 'NAME' }, { label: 'ADDED' }]}
        tableData={tableData}
        emptyText="No files uploaded"
        data-testid="uploaded-files-table"
        allowRowSelect={true}
        selectedRows={selectedFiles}
        onRowClick={(row, _, e) => {
          if (
            (e.target as HTMLElement).closest(
              '[data-testid*="uploaded-evidence-file-link"]',
            )
          ) {
            return;
          }
          if (selectedFiles.includes(row)) {
            setSelectedFiles(selectedFiles.filter((id) => id !== row));
          } else {
            setSelectedFiles([...selectedFiles, row]);
          }
        }}
        isFullWidth
        small
        hasStickyHeader={false}
        selectAll={handleSelectAllFiles}
        allSelected={isAllFilesSelected()}
      />
    </UploadedFilesContainer>
  );
};
